import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuthorProfile {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  verification_status: string | null;
  city: string | null;
  user_type: string | null;
}

/**
 * Resolves profile pictures / names for a set of author, owner or seller ids so every
 * card across the platform can show who it belongs to.
 */
export function useAuthors(ids: (string | null | undefined)[]) {
  const unique = Array.from(new Set(ids.filter(Boolean) as string[])).sort();
  const key = unique.join(",");

  const query = useQuery({
    queryKey: ["authors", key],
    enabled: unique.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, full_name, avatar_url, verification_status, city, user_type")
        .in("id", unique);
      if (error) throw error;
      return (data ?? []) as AuthorProfile[];
    },
  });

  const map: Record<string, AuthorProfile> = {};
  (query.data ?? []).forEach((p) => { map[p.id] = p; });

  const getAuthor = (id: string | null | undefined) => {
    const p = id ? map[id] : undefined;
    return {
      id: id ?? "unknown",
      name: p?.full_name || p?.username || "Metro City member",
      handle: p?.username ?? null,
      avatarUrl: p?.avatar_url ?? null,
      verified: p?.verification_status === "verified",
      meta: p?.city ?? null,
    };
  };

  return { authors: map, getAuthor, isLoading: query.isLoading };
}
